// Team-member (dealmaker) profile — there's no DealCloud "user" record to open,
// so this is an Atlas-native rollup: who they are + their book of business
// (KPIs, pipeline by stage, and their engagements) derived from
// listEngagements({dealmaker_id}). Linked from clickable dealmaker names in
// Analytics (and anywhere else via window.AtlasNav.goToUser).

const _money = (n) => (window.fmtMoney ? window.fmtMoney(Number(n) || 0) : '$' + (Number(n) || 0).toLocaleString());
const _compact = (n) => {
  const v = Number(n) || 0;
  if (Math.abs(v) >= 1e6) return '$' + (v / 1e6).toFixed(1) + 'M';
  if (Math.abs(v) >= 1e3) return '$' + Math.round(v / 1e3) + 'K';
  return '$' + Math.round(v).toLocaleString();
};

const _STAGE_HUE = {
  prospect: 'slate', qualified: 'slate', interested: 'teal', ds_call: 'teal', hot_lead: 'teal',
  blast: 'violet', bs_call: 'violet', data_gathering: 'amber', valuation_presented: 'amber',
  loi: 'accent', won: 'green',
};

const UPStat = ({ label, value, hue = 'accent', icon, phone }) => {
  const C = (window.Icon && window.Icon[icon]) || null;
  const BG = { accent: 'var(--accent-soft)', teal: 'var(--teal-bg)', green: 'var(--green-bg)', violet: 'var(--violet-bg)', amber: 'var(--amber-bg)' };
  const FG = { accent: 'var(--accent-text)', teal: 'var(--teal-fg)', green: 'var(--green-fg)', violet: 'var(--violet-fg)', amber: 'var(--amber-fg)' };
  return (
    <div style={{ background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 'var(--radius-lg)', padding: '12px 14px' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 7, marginBottom: 8 }}>
        <span style={{ width: 24, height: 24, borderRadius: 7, background: BG[hue], color: FG[hue], display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}>{C && <C size={13} />}</span>
        <span style={{ fontSize: phone ? 12.5 : 11, color: 'var(--text-3)', fontWeight: 500, ...(phone ? { lineHeight: 1.25 } : null) }}>{label}</span>
      </div>
      <div style={{ fontSize: 22, fontWeight: 600, color: 'var(--text)', fontFamily: 'var(--font-display)', letterSpacing: '-0.01em' }} className="num">{value}</div>
    </div>
  );
};

// Admin-only offboarding / activity-history section on the member profile.
// Lazy: nothing is fetched until the admin expands it. Reads
// GET /api/team/members/{id}/offboarding-report (view_team_activity) — 52
// weeks of activity with a detected inflection point, integration coverage,
// and transition counts. Sparklines reuse window.TPSpark from Team Pulse.
const UP_OFF_METRICS = [
  { key: 'emails_sent',       label: 'Outbound emails' },
  { key: 'meetings_recorded', label: 'Recorded meetings' },
  { key: 'notes_logged',      label: 'Notes logged' },
];

const UPOffboarding = ({ userId, memberName, goToCompany }) => {
  const { useState } = React;
  const [open, setOpen] = useState(false);
  const [report, setReport] = useState(null);
  const [err, setErr] = useState('');
  const [downloading, setDownloading] = useState(false);

  const load = () => {
    if (report || err) return;
    window.AtlasApi.offboardingReport(userId)
      .then(setReport)
      .catch((e) => setErr((e && (e.message || e.detail)) || 'Failed to load report'));
  };

  // Server-rendered standalone HTML (static SVG charts, no JS) — the
  // shareable artifact version of this section. fetch → blob so the auth
  // header rides along; a bare <a href> can't carry it.
  const download = () => {
    if (downloading) return;
    setDownloading(true);
    window.AtlasApi.downloadOffboardingReport(userId)
      .catch((e) => {
        // Toast, not setErr — a failed download shouldn't wedge the inline view.
        const msg = (e && (e.message || e.detail)) || 'Failed to download report';
        if (window.pushToast) window.pushToast({ type: 'error', title: msg });
      })
      .finally(() => setDownloading(false));
  };

  // The handoff-scan panel (views/handoff_scan.jsx) lives at the bottom of
  // this same section — its runs-list fetch is light, so it always renders.
  // iPad: the scan's 920px table scrolls inside the section rather than
  // pushing the whole profile sideways in portrait.
  const touch = !!(window.AtlasDevice && window.AtlasDevice.touch);
  const handoff = (
    <div style={{ borderTop: '1px solid var(--border)', padding: '12px 14px', ...(touch ? { overflowX: 'auto' } : null) }}>
      <window.HandoffScanPanel userId={userId} memberName={memberName} goToCompany={goToCompany} />
    </div>
  );

  return (
    <window.Section title="Activity & offboarding report"
      action={
        <window.Btn size="sm" variant="outline" disabled={downloading} onClick={download}
          title="Download as a standalone HTML file (shareable, no login needed to view)">
          {downloading ? 'Preparing…' : 'Download report'}
        </window.Btn>
      }>
      {!open ? (
        <div style={{ padding: '12px 14px' }}>
          <window.Btn size="sm" onClick={() => { setOpen(true); load(); }}>
            Generate report
          </window.Btn>
        </div>
      ) : err ? (
        <div style={{ padding: '12px 14px', fontSize: 12.5, color: 'var(--rose-fg)' }}>{err}</div>
      ) : !report ? (
        <div style={{ padding: 14 }}><window.SkeletonRows rows={3} /></div>
      ) : (
        <div style={{ padding: '12px 14px', display: 'flex', flexDirection: 'column', gap: 14 }}>
          {/* Inflection callouts — the "when did efforts change?" answer. */}
          {Object.keys(report.inflections || {}).length > 0 && (
            <div style={{ background: 'var(--amber-bg)', borderRadius: 6, padding: '10px 12px', display: 'flex', flexDirection: 'column', gap: 4 }}>
              {Object.entries(report.inflections).map(([k, inf]) => (
                <div key={k} style={{ fontSize: 12.5, color: 'var(--amber-fg)' }}>
                  <b>{(UP_OFF_METRICS.find((m) => m.key === k)?.label || k)}</b>
                  {' '}fell from a median of {inf.before_median}/wk to {inf.after_median}/wk
                  {' '}around the week of <b>{window.fmtDate(inf.week)}</b>.
                </div>
              ))}
            </div>
          )}

          {/* 52-week sparklines */}
          <div style={{ display: 'flex', gap: 22, overflowX: 'auto' }}>
            {UP_OFF_METRICS.map(({ key, label }) => (
              <div key={key} style={{ flexShrink: 0 }}>
                <div style={{ fontSize: 10.5, fontWeight: 600, letterSpacing: '0.04em', textTransform: 'uppercase', color: 'var(--text-4)', marginBottom: 5 }}>{label} · 52w</div>
                <window.TPSpark vals={report.metrics[key]} weeks={report.weeks} coverageIso={null} />
              </div>
            ))}
          </div>

          {/* Transition checklist counts */}
          <div style={{ display: 'flex', gap: 18, flexWrap: 'wrap', fontSize: 12.5, color: 'var(--text-2)' }}>
            <span><b style={{ color: 'var(--text)' }}>{report.book.active_total.toLocaleString()}</b> active engagements to reassign</span>
            <span><b style={{ color: 'var(--text)' }}>{(report.book.stages || []).reduce((s, x) => s + (x.with_next_action || 0), 0).toLocaleString()}</b> with a future next action</span>
            <span title={`${report.contacts_owned.toLocaleString()} owned in total incl. the bulk-assigned prospect universe`}>
              <b style={{ color: 'var(--text)' }}>{(report.contacts_engaged ?? report.contacts_owned).toLocaleString()}</b> engaged contacts owned</span>
            <span><b style={{ color: 'var(--text)' }}>{report.open_tasks.toLocaleString()}</b> open tasks</span>
          </div>

          {/* Integration coverage — context for reading the zeros. */}
          <div style={{ fontSize: 11.5, color: 'var(--text-4)' }}>
            {(report.integrations || []).length === 0
              ? 'No integrations connected — email/meeting history for this member exists only via colleagues’ synced mailboxes.'
              : (report.integrations || []).map((c) => (
                <span key={c.provider} style={{ marginRight: 14 }}>
                  {c.provider}: connected {window.fmtDate(c.created_at)}
                  {c.active === false ? ' (inactive)' : ''}
                </span>
              ))}
          </div>
        </div>
      )}
      {handoff}
    </window.Section>
  );
};

// Files on this member's profile — documents that belong to the PERSON, not
// to a company or deal: a OneNote notebook export, a territory list, a
// reference deck. Personal by design: rendered for the member themself and
// admins only (the API enforces the same rule on list / upload / delete /
// download, so this is presentation, not the gate). GET/POST/DELETE
// /api/team/members/{id}/files; downloads reuse the signed-URL path every
// other attachment chip uses. Same 50 MB per-file cap as note uploads.
const _fmtBytes = (n) => {
  if (n == null) return '';
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(0)} KB`;
  return `${(n / 1024 / 1024).toFixed(1)} MB`;
};

const UPFiles = ({ userId, isSelf, phone }) => {
  const { useState, useEffect, useRef } = React;
  const [items, setItems] = useState(null);
  const [err, setErr] = useState('');
  const [busy, setBusy] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const inputRef = useRef(null);
  const toast = (t) => window.pushToast && window.pushToast(t);

  const load = () => window.AtlasApi.listMemberFiles(userId)
    .then((r) => { setItems(Array.isArray(r) ? r : []); setErr(''); })
    .catch((e) => { setItems([]); setErr((e && (e.message || e.detail)) || 'Failed to load files'); });
  useEffect(() => { setItems(null); setErr(''); load(); }, [userId]);

  const upload = async (fileList) => {
    if (!fileList || fileList.length === 0 || busy) return;
    setBusy(true);
    try {
      const created = await window.AtlasApi.uploadMemberFiles(userId, fileList);
      toast({ type: 'success', title: `Uploaded ${created.length} file${created.length === 1 ? '' : 's'}` });
      await load();
    } catch (e) {
      toast({ type: 'error', title: 'Upload failed', body: e.message });
    } finally {
      setBusy(false);
      if (inputRef.current) inputRef.current.value = '';
    }
  };

  const remove = async (att) => {
    if (!window.confirm(`Remove "${att.original_filename}" from ${isSelf ? 'your' : 'this'} profile? The file is deleted.`)) return;
    try {
      await window.AtlasApi.deleteMemberFile(userId, att.id);
      setItems((cur) => (cur || []).filter((x) => x.id !== att.id));
    } catch (e) {
      toast({ type: 'error', title: 'Could not remove file', body: e.message });
    }
  };

  const rows = items || [];
  return (
    <window.Section title={`Files${items ? ` · ${items.length}` : ''}`}
      action={
        <>
          <input ref={inputRef} type="file" multiple style={{ display: 'none' }}
            onChange={(e) => upload(e.target.files)} />
          <window.Btn size="sm" variant="outline" disabled={busy} icon={<window.Icon.Plus size={12} />}
            title="Files here belong to this person, not to a deal — visible to them and admins only. 50 MB per file."
            onClick={() => inputRef.current && inputRef.current.click()}>
            {busy ? 'Uploading…' : 'Upload'}
          </window.Btn>
        </>
      }>
      <div
        onDragEnter={(e) => { e.preventDefault(); setDragOver(true); }}
        onDragOver={(e) => { e.preventDefault(); }}
        onDragLeave={(e) => { if (e.currentTarget === e.target) setDragOver(false); }}
        onDrop={(e) => { e.preventDefault(); setDragOver(false); if (e.dataTransfer?.files?.length) upload(e.dataTransfer.files); }}
        style={{ position: 'relative', outline: dragOver ? '2px dashed var(--accent)' : 'none', outlineOffset: -2, borderRadius: '0 0 10px 10px' }}>
        {items === null ? (
          <div style={{ padding: 14 }}><window.SkeletonRows rows={2} /></div>
        ) : err ? (
          <div style={{ padding: '12px 14px', fontSize: 12.5, color: 'var(--rose-fg)' }}>{err}</div>
        ) : rows.length === 0 ? (
          <div style={{ padding: '18px 14px', textAlign: 'center', color: 'var(--text-3)', fontSize: 12.5 }}>
            No files yet. {phone ? 'Use Upload' : 'Drop files here or use Upload'}.
          </div>
        ) : rows.map((att, i) => {
          const available = att.storage_status === 'available' && att.storage_uri;
          const hue = (window.ATTACHMENT_EXT_HUE && window.ATTACHMENT_EXT_HUE[(att.file_extension || '').toLowerCase()]) || 'slate';
          const h = (window.HUE && window.HUE[hue]) || (window.HUE && window.HUE.slate) || { bg: 'var(--surface-2)', fg: 'var(--text-3)' };
          const when = att.created_at ? window.fmtDate(att.created_at) : null;
          return (
            <div key={att.id} style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '9px 14px', borderTop: i === 0 ? 'none' : '1px solid var(--border)' }}>
              <span style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: 32, height: 32, borderRadius: 5, flexShrink: 0, background: available ? h.bg : 'var(--surface-2)', color: available ? h.fg : 'var(--text-4)', fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                {(att.file_extension || '·').slice(0, 4)}
              </span>
              <div style={{ flex: 1, minWidth: 0 }}>
                {available ? (
                  <button onClick={() => window.AtlasApi.openAttachment(att.id, att.original_filename)} title="Download"
                    style={{ border: 'none', background: 'transparent', cursor: 'pointer', color: 'var(--text)', fontSize: 13, fontWeight: 500, padding: 0, textAlign: 'left', maxWidth: '100%', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', display: 'block' }}>
                    {att.original_filename}
                  </button>
                ) : (
                  <div style={{ fontSize: 13, fontWeight: 500, color: 'var(--text-3)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{att.original_filename}</div>
                )}
                <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 11, color: 'var(--text-4)', marginTop: 2, flexWrap: 'wrap' }}>
                  {att.size_bytes != null && <span>{_fmtBytes(att.size_bytes)}</span>}
                  {when && <><span>·</span><span>{when}</span></>}
                  {att.uploaded_by_name && <><span>·</span><span>uploaded by {att.uploaded_by_name}</span></>}
                  {!available && <><span>·</span><window.Pill hue="slate" sz="sm">missing</window.Pill></>}
                </div>
              </div>
              {available && (
                <window.Btn size="xs" variant="ghost" icon={<window.Icon.Download size={12} />} title="Download"
                  onClick={() => window.AtlasApi.openAttachment(att.id, att.original_filename)} />
              )}
              <window.Btn size="xs" variant="ghost" icon={<window.Icon.Trash size={12} />} title="Remove from profile"
                onClick={() => remove(att)} />
            </div>
          );
        })}
        {(dragOver || busy) && (
          <div style={{ position: 'absolute', inset: 0, background: 'var(--accent-soft)', border: '1px dashed var(--accent)', borderRadius: '0 0 10px 10px', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--accent-text)', fontSize: 12, fontWeight: 600, pointerEvents: busy ? 'auto' : 'none', zIndex: 5 }}>
            {busy ? 'Uploading…' : 'Drop to add to this profile'}
          </div>
        )}
      </div>
    </window.Section>
  );
};

const UserProfile = ({ userId, teamMembers, stages, currentRole, selfId, setDealId, setCompanyId, onViewEngagements, back }) => {
  const { useState, useEffect, useMemo } = React;
  const device = window.useDevice ? window.useDevice() : (window.AtlasDevice || {});
  const [member, setMember] = useState(() => (teamMembers || []).find((m) => m.id === userId) || null);
  const [engs, setEngs] = useState(null);   // adapted engagements
  const [err, setErr] = useState('');
  const [limit, setLimit] = useState(40);

  // Resolve the member (prop list first, then a fetch fallback for deep links).
  useEffect(() => {
    const found = (teamMembers || []).find((m) => m.id === userId);
    if (found) { setMember(found); return; }
    window.AtlasApi.listTeamMembers()
      .then((m) => setMember((Array.isArray(m) ? m : []).find((x) => x.id === userId) || null))
      .catch(() => setMember(null));
  }, [userId, teamMembers]);

  useEffect(() => {
    setEngs(null); setErr(''); setLimit(40);
    window.AtlasApi.listEngagements({ dealmaker_id: userId, limit: 500 })
      .then((r) => setEngs(((r && r.items) || []).map(window.AtlasData.__adaptEngagement)))
      .catch((e) => { setErr((e && (e.message || e.detail)) || 'Failed to load engagements'); setEngs([]); });
  }, [userId]);

  const stats = useMemo(() => {
    const items = engs || [];
    const active = items.filter((i) => !i.archived && !i.exitName);
    const won = items.filter((i) => i.stage === 'won' || i.closedDate);
    const fees = active.reduce((s, i) => s + (Number(i.size) || 0), 0);
    const companies = new Set(items.map((i) => i.target).filter(Boolean));
    return { total: items.length, active, won: won.length, fees, companies: companies.size };
  }, [engs]);

  const byStage = useMemo(() => {
    const m = new Map();
    (stats.active || []).forEach((i) => { const k = i.stageName || i.stage || '—'; m.set(k, (m.get(k) || 0) + 1); });
    return [...m.entries()].map(([stage, count]) => ({ stage, count })).sort((a, b) => b.count - a.count);
  }, [stats.active]);
  const stageMax = Math.max(1, ...byStage.map((s) => s.count));

  const activeSorted = useMemo(
    () => [...(stats.active || [])].sort((a, b) => (Number(b.size) || 0) - (Number(a.size) || 0)),
    [stats.active]);

  const name = member?.full_name || 'Team member';
  const loading = engs === null;
  // Files are personal: the member themself and admins. The API is the gate;
  // this only decides whether to draw the section at all.
  const isAdmin = (currentRole || '').toLowerCase() === 'admin';
  const isSelf = !!selfId && String(selfId) === String(userId);

  // ── phone ─────────────────────────────────────────────────────────────────
  // One readable column: who they are (email is a mailto link), the four
  // book-of-business numbers as a 2×2 grid, stage counts with a bar under
  // each, and the active engagements as tappable rows. The admin offboarding
  // report / handoff scan stays on desktop.
  if (device.phone) {
    const PRow = window.PhoneRow;
    const PSection = window.PhoneSectionHeader;
    return (
      <window.PhoneScroll>
        <div style={{ padding: '18px 16px 14px', background: 'var(--surface)', borderBottom: '1px solid var(--border)' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
            <window.Avatar name={name} size={52} />
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontSize: 20, fontWeight: 600, color: 'var(--text)', fontFamily: 'var(--font-display)', lineHeight: 1.25, overflowWrap: 'anywhere' }}>{name}</div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap', marginTop: 5 }}>
                {member?.role && <window.Pill hue="violet" sz="md">{window.humanizeCol ? window.humanizeCol(member.role) : member.role}</window.Pill>}
                {member && member.active === false && <window.Pill hue="slate" sz="md">inactive</window.Pill>}
                {member?.team_name && <span style={{ fontSize: 14, color: 'var(--text-3)' }}>{member.team_name}</span>}
              </div>
            </div>
          </div>
          {member?.email && (
            <a href={`mailto:${member.email}`}
              style={{ display: 'flex', alignItems: 'center', gap: 8, minHeight: 44, marginTop: 6, fontSize: 15, color: 'var(--accent-text)', textDecoration: 'none', overflowWrap: 'anywhere' }}>
              <window.Icon.Mail size={16} /> {member.email}
            </a>
          )}
          <window.Btn size="md" variant="accent" icon={<window.Icon.Pipeline size={14} />}
            style={{ width: '100%', justifyContent: 'center', marginTop: member?.email ? 4 : 12 }}
            onClick={() => onViewEngagements && onViewEngagements(userId, name)}>View all engagements</window.Btn>
        </div>

        {loading ? (
          <div style={{ marginTop: 14 }}><window.SkeletonRows rows={6} /></div>
        ) : (
          <>
            {err && (
              <div style={{ margin: '14px 16px 0', background: 'var(--rose-bg)', color: 'var(--rose-fg)', borderRadius: 8, padding: '10px 12px', fontSize: 14 }}>{err}</div>
            )}

            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: 10, padding: '14px 16px 0' }}>
              <UPStat phone label="Active engagements" value={stats.active.length.toLocaleString()} hue="accent" icon="Pipeline" />
              <UPStat phone label="Won deals" value={stats.won.toLocaleString()} hue="green" icon="Check" />
              <UPStat phone label="Pipeline est. fees" value={_compact(stats.fees)} hue="teal" icon="Money" />
              <UPStat phone label="Companies" value={stats.companies.toLocaleString()} hue="violet" icon="Building" />
            </div>

            {byStage.length > 0 && PSection && (
              <>
                <PSection title="Pipeline by stage" />
                <div style={{ background: 'var(--surface)', borderTop: '1px solid var(--border)', borderBottom: '1px solid var(--border)', padding: '0 16px' }}>
                  {byStage.map((st, i) => (
                    <div key={st.stage} style={{ padding: '10px 0', borderTop: i ? '1px solid var(--border)' : 'none' }}>
                      <div style={{ display: 'flex', alignItems: 'baseline', gap: 8 }}>
                        <span style={{ flex: 1, minWidth: 0, fontSize: 15, color: 'var(--text)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{st.stage}</span>
                        <span className="num" style={{ fontSize: 15, fontWeight: 600, color: 'var(--text-2)' }}>{st.count}</span>
                      </div>
                      <div style={{ marginTop: 6, background: 'var(--surface-2)', borderRadius: 4, height: 6, overflow: 'hidden' }}>
                        <div style={{ width: `${(st.count / stageMax) * 100}%`, height: '100%', background: 'var(--accent)' }} />
                      </div>
                    </div>
                  ))}
                </div>
              </>
            )}

            {PSection && <PSection title="Active engagements" count={stats.active.length} />}
            {activeSorted.length === 0 ? (
              window.PhoneEmpty
                ? <window.PhoneEmpty title="No active engagements" body={stats.total ? 'Their engagements are all closed or archived.' : null} />
                : <div style={{ padding: 24, textAlign: 'center', color: 'var(--text-3)', fontSize: 15 }}>No active engagements.</div>
            ) : PRow && (
              <div style={{ borderTop: '1px solid var(--border)' }}>
                {activeSorted.slice(0, limit).map((e) => (
                  <PRow key={e.id} title={e.name || '—'}
                    subtitle={[e.targetName, e.stageName || e.stage].filter(Boolean).join(' · ') || null}
                    meta={e.size != null && Number(e.size) ? _compact(e.size) : null}
                    onClick={() => setDealId(e.id)} />
                ))}
                {activeSorted.length > limit && (
                  <button type="button" onClick={() => setLimit((l) => l + 40)}
                    style={{ width: '100%', minHeight: 48, border: 'none', borderBottom: '1px solid var(--border)', background: 'var(--surface)', cursor: 'pointer', fontSize: 15, color: 'var(--accent-text)', fontFamily: 'inherit' }}>
                    Show more ({activeSorted.length - limit} more)
                  </button>
                )}
              </div>
            )}

            {(isAdmin || isSelf) && (
              <div style={{ padding: '18px 16px 0' }}>
                <UPFiles userId={userId} isSelf={isSelf} phone />
              </div>
            )}
          </>
        )}
      </window.PhoneScroll>
    );
  }

  // ── header ────────────────────────────────────────────────────────────────
  const header = (
    <header style={{ padding: '14px 16px 12px', background: 'var(--surface)', borderBottom: '1px solid var(--border)' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 11.5, color: 'var(--text-3)', marginBottom: 8 }}>
        <button onClick={back} style={{ border: 'none', background: 'transparent', cursor: 'pointer', color: 'var(--text-3)', padding: 0, display: 'inline-flex', alignItems: 'center', gap: 4 }}>
          <window.Icon.ChevronL size={12} /> Workspace
        </button>
        <window.Icon.ChevronR size={11} color="var(--text-4)" />
        <span>Team</span>
      </div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
        <window.Avatar name={name} size={40} />
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
            <h1 style={{ fontSize: 19, fontWeight: 600, color: 'var(--text)' }}>{name}</h1>
            {member?.role && <window.Pill hue="violet" sz="sm">{window.humanizeCol ? window.humanizeCol(member.role) : member.role}</window.Pill>}
            {member && member.active === false && <window.Pill hue="slate" sz="sm">inactive</window.Pill>}
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginTop: 3, fontSize: 12, color: 'var(--text-3)' }}>
            {member?.email && <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5 }}><window.Icon.Mail size={12} /> {member.email}</span>}
            {member?.team_name && <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5 }}><window.Icon.Users size={12} /> {member.team_name}</span>}
          </div>
        </div>
        <window.Btn size="sm" variant="accent" icon={<window.Icon.Pipeline size={12} />}
          onClick={() => onViewEngagements && onViewEngagements(userId, name)}>View all engagements</window.Btn>
      </div>
    </header>
  );

  if (loading) {
    return (
      <div style={{ display: 'flex', flexDirection: 'column', height: '100%', overflow: 'hidden' }}>
        {header}
        <div style={{ padding: 16 }}><window.SkeletonRows rows={4} /></div>
      </div>
    );
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%', overflow: 'hidden' }}>
      {header}
      <div style={{ flex: 1, overflow: 'auto', padding: 16, display: 'flex', flexDirection: 'column', gap: 16 }}>
        {err && <div style={{ background: 'var(--rose-bg)', color: 'var(--rose-fg)', border: '1px solid var(--border)', borderRadius: 8, padding: '10px 12px', fontSize: 12.5 }}>{err}</div>}

        {/* KPI strip */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: 10 }}>
          <UPStat label="Active engagements" value={stats.active.length.toLocaleString()} hue="accent" icon="Pipeline" />
          <UPStat label="Won deals" value={stats.won.toLocaleString()} hue="green" icon="Check" />
          <UPStat label="Pipeline est. fees" value={_compact(stats.fees)} hue="teal" icon="Money" />
          <UPStat label="Companies" value={stats.companies.toLocaleString()} hue="violet" icon="Building" />
        </div>

        {/* Admin-only: activity history + offboarding report + handoff scan */}
        {isAdmin && (
          <UPOffboarding userId={userId} memberName={name} goToCompany={setCompanyId} />
        )}

        {/* The person's own files — theirs and the admins' to see */}
        {(isAdmin || isSelf) && (
          <UPFiles userId={userId} isSelf={isSelf} />
        )}

        {/* Pipeline by stage */}
        {byStage.length > 0 && (
          <window.Section title="Pipeline by stage">
            <div style={{ padding: '10px 14px', display: 'flex', flexDirection: 'column', gap: 7 }}>
              {byStage.map((s) => (
                <div key={s.stage} style={{ display: 'grid', gridTemplateColumns: '160px 1fr 36px', alignItems: 'center', gap: 10 }}>
                  <span style={{ fontSize: 12, color: 'var(--text-2)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{s.stage}</span>
                  <div style={{ background: 'var(--surface-2)', borderRadius: 4, height: 8, overflow: 'hidden' }}>
                    <div style={{ width: `${(s.count / stageMax) * 100}%`, height: '100%', background: 'var(--accent)' }} />
                  </div>
                  <span style={{ fontSize: 12, color: 'var(--text-3)', textAlign: 'right' }} className="num">{s.count}</span>
                </div>
              ))}
            </div>
          </window.Section>
        )}

        {/* Their active engagements */}
        <window.Section title={`Active engagements · ${stats.active.length}`}>
          {activeSorted.length === 0 ? (
            <div style={{ padding: 24, textAlign: 'center', color: 'var(--text-3)', fontSize: 13 }}>No active engagements.</div>
          ) : (
            <div>
              <div style={{ display: 'grid', gridTemplateColumns: '1.6fr 1.3fr 0.9fr 0.7fr', borderBottom: '1px solid var(--border)', background: 'var(--surface-2)' }}>
                {['Engagement', 'Company', 'Stage', 'Est. fees'].map((h, i) => (
                  <div key={h} style={{ padding: '7px 12px', fontSize: 11, fontWeight: 600, color: 'var(--text-3)', textAlign: i === 3 ? 'right' : 'left' }}>{h}</div>
                ))}
              </div>
              {activeSorted.slice(0, limit).map((e) => (
                <div key={e.id} style={{ display: 'grid', gridTemplateColumns: '1.6fr 1.3fr 0.9fr 0.7fr', borderBottom: '1px solid var(--border)', alignItems: 'center' }}>
                  <div style={{ padding: '8px 12px', minWidth: 0 }}>
                    <button onClick={() => setDealId(e.id)} title={e.name}
                      style={{ border: 'none', background: 'transparent', cursor: 'pointer', color: 'var(--accent-text)', fontSize: 12.5, padding: 0, textAlign: 'left', maxWidth: '100%', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{e.name || '—'}</button>
                  </div>
                  <div style={{ padding: '8px 12px', minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {e.target
                      ? <button onClick={() => setCompanyId(e.target)} style={{ border: 'none', background: 'transparent', cursor: 'pointer', color: 'var(--accent-text)', fontSize: 12, padding: 0 }}>{e.targetName || '—'}</button>
                      : <span style={{ fontSize: 12, color: 'var(--text-3)' }}>{e.targetName || '—'}</span>}
                  </div>
                  <div style={{ padding: '8px 12px' }}>
                    <window.Pill hue={_STAGE_HUE[e.stage] || 'slate'} sz="sm">{e.stageName || e.stage || '—'}</window.Pill>
                  </div>
                  <div style={{ padding: '8px 12px', fontSize: 12, color: 'var(--text-2)', textAlign: 'right', fontVariantNumeric: 'tabular-nums' }} className="num">{e.size != null ? _money(e.size) : '—'}</div>
                </div>
              ))}
              {activeSorted.length > limit && (
                <button onClick={() => setLimit((l) => l + 40)} style={{ width: '100%', padding: 8, border: 'none', background: 'var(--surface)', cursor: 'pointer', fontSize: 11.5, color: 'var(--accent-text)' }}>
                  Show more ({activeSorted.length - limit} more)
                </button>
              )}
            </div>
          )}
        </window.Section>
      </div>
    </div>
  );
};

window.UserProfile = UserProfile;
